import * as React from 'react'

import {
  Box,
  Button,
  type Colors,
  Tag,
  TagColor,
  Text,
} from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import DocumentType from 'modules/core/components/DocumentType'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'

import getDropItemStyles from 'modules/expenses/lib/expensables/dropItemStyles'

export interface UploadFile {
  id: string | null
  name: string
  size: string
  type: string
  preview: string
}

interface DropItemProps {
  file: UploadFile
  showInViewer?: boolean
  onSelectFileForViewer?: (file: UploadFile) => void
  removable?: boolean
  signedDocument?: boolean
  signingDocument?: boolean
  showAdeTag?: boolean
  adeArchived?: boolean
  onRemove?: ({ name, size, type, preview, id }: UploadFile) => void
  date?: string
  downloadable?: boolean
}

const DropItem = ({
  file: { name, size, type, preview, id = null },
  showInViewer,
  onSelectFileForViewer,
  removable = false,
  onRemove,
  signedDocument = false,
  signingDocument = false,
  showAdeTag = false,
  adeArchived = false,
  date,
  downloadable = true,
}: DropItemProps) => {
  const [isHovered, setIsHovered] = React.useState(false)

  const { background } = getDropItemStyles(isHovered)

  if (showInViewer === undefined) {
    showInViewer = !type.includes('xml')
  }

  return (
    <Box
      flexDirection="row"
      justifyContent="spaceBetween"
      alignItems="center"
      background={background as Colors}
      height="s64"
      padding="s8"
      borderRadius={{ all: 'abs008' }}
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey400',
        },
      }}
      gap="s8"
      onClick={() =>
        onSelectFileForViewer && onSelectFileForViewer({ name, size, type, preview, id })
      }
      cursor="pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box flexDirection="row" gap="s12">
        <Box justifyContent="center">
          <DocumentType mimetype={type} />
        </Box>
        <Box justifyContent="center">
          <Box
            flexDirection="column"
            justifyContent="flexStart"
            alignItems="flexStart"
            gap="s4"
            maxWidth="s240"
          >
            <Text.Normal ellipsis weight="semibold" size="200">
              {name}
            </Text.Normal>

            {signedDocument && (
              <Text.Normal ellipsis size="200" color="grey700">
                {moment.utc(date).format('DD MMMM, YYYY')}
              </Text.Normal>
            )}
          </Box>
        </Box>
      </Box>
      <Box flexDirection="row" gap="s8">
        {showAdeTag && !signedDocument && (
          <Box justifyContent="center">
            {adeArchived ? (
              <Tag type={TagColor.lime}>
                {i18n.t('expenses_v2.forms.fields.signed_document.ade_archived')}
              </Tag>
            ) : (
              <Tag type={TagColor.grey}>
                {i18n.t('expenses_v2.forms.fields.signed_document.ade_archiving')}
              </Tag>
            )}
          </Box>
        )}
        {signedDocument && (
          <Box justifyContent="center">
            <Tag type={TagColor.lime}>
              {i18n.t('expenses_v2.forms.fields.signed_document.signed')}
            </Tag>
          </Box>
        )}
        {!signedDocument && signingDocument && (
          <Box justifyContent="center">
            <Tag type={TagColor.grey}>
              {i18n.t('expenses_v2.forms.fields.signed_document.signing')}
            </Tag>
          </Box>
        )}
        {!showInViewer && downloadable ? (
          <Button.Router
            type="secondary"
            to={preview}
            rel="noopener noreferrer"
            download
            blankTarget
            iconName="download"
          />
        ) : (
          !!onSelectFileForViewer && (
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Button.Decorative type="secondary" iconName="ellipsis" />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="end">
                <PopoverMenu.HrefOption
                  title={i18n.t('expenses_v2.buttons.download')}
                  href={preview}
                  download
                  target="_blank"
                />
                {removable && onRemove && (
                  <Box
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                  >
                    <PopoverMenu.TouchableOption
                      title={i18n.t('expenses_v2.buttons.delete')}
                      onClick={() => {
                        onRemove({ name, size, type, preview, id })
                      }}
                    />
                  </Box>
                )}
              </PopoverMenu.Content>
            </PopoverMenu>
          )
        )}
      </Box>
    </Box>
  )
}

export default observer(DropItem)
